import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faMusic, 
  faImage, 
  faMicrophone, 
  faEllipsisH,
  faCheckCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import styles from './BatchProcessModal.module.css';
import { BatchProcessOptions, ProcessingStatus, DraftPrompt } from '../types/batch';
import { getApiUrl } from '../../../config/api';
import { processBatchPrompts } from '../utils/batchProcessingService';

interface BatchProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => Promise<void>;  // Changed from onProcess - just reload prompts
  projectId: string;
}

const BatchProcessModal: React.FC<BatchProcessModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  projectId
}) => {
  const { t } = useTranslation('promptGenerator');
  
  // State for draft prompts
  const [draftPrompts, setDraftPrompts] = useState<DraftPrompt[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);

  // Fetch draft prompts when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchDraftPrompts();
    }
  }, [isOpen, projectId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset processing state
      setIsProcessing(false);
      setProcessingStatus([]);
      setTotalPromptsToProcess(0);
      
      // Reset "Apply to all" checkboxes
      setApplyStyleToAll(false);
      setApplyPurposeToAll(false);
      
      // Reset options to defaults
      setMusicOptions({ style: 'cinematic', purpose: 'storytelling' });
      setImageOptions({ style: 'cinematic', purpose: 'storytelling' });
      setNarrationOptions({ style: 'professional', purpose: 'storytelling' });
      setOthersOptions({ style: 'artistic', purpose: 'entertainment' });
    }
  }, [isOpen]);

  const fetchDraftPrompts = async () => {
    setIsLoadingPrompts(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/prompt-generator/prompts/${projectId}`), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        }
      });

      const result = await response.json();
      if (result.success && result.data) {
        console.log('📋 Fetched prompts:', result.data.length, 'prompts');
        console.log('📋 First prompt sample:', result.data[0]);
        setDraftPrompts(result.data);
      }
    } catch (error) {
      console.error('Error fetching draft prompts:', error);
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  // Calculate prompt counts for each type - ONLY COUNT DRAFTS
  // Uses same logic as the filter service (first word of title)
  const getPromptCountByType = (type: string): number => {
    return draftPrompts.filter(prompt => {
      // CRITICAL: Only count draft prompts (not already generated)
      const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
      if (!isDraft) return false;
      
      const title = (prompt.title || '').toLowerCase();
      const firstWord = title.split(' ')[0];
      
      switch(type) {
        case 'music':
          return firstWord === 'music';
        case 'image':
          return firstWord === 'image';
        case 'narration':
          return firstWord === 'narration' || firstWord === 'voice';
        case 'others':
          // Others = anything that doesn't match known types
          return !['image', 'video', 'music', 'sound', 'sfx', 'narration', 'voice', 'character'].includes(firstWord);
        default:
          return false;
      }
    }).length;
  };

  const promptCounts = {
    music: getPromptCountByType('music'),
    image: getPromptCountByType('image'),
    narration: getPromptCountByType('narration'),
    others: getPromptCountByType('others')
  };

  // Only show types that have existing prompts
  const availableTypes = Object.entries(promptCounts)
    .filter(([_, count]) => count > 0)
    .map(([type]) => type);

  // Type selections - only enable if type has prompts
  const [selectedTypes, setSelectedTypes] = useState({
    music: promptCounts.music > 0,
    image: promptCounts.image > 0,
    narration: promptCounts.narration > 0,
    others: promptCounts.others > 0
  });

  // Options for each type
  const [musicOptions, setMusicOptions] = useState({
    style: 'cinematic',
    purpose: 'storytelling'
  });

  const [imageOptions, setImageOptions] = useState({
    style: 'cinematic',
    purpose: 'storytelling'
  });

  const [narrationOptions, setNarrationOptions] = useState({
    style: 'professional',
    purpose: 'storytelling'
  });

  const [othersOptions, setOthersOptions] = useState({
    style: 'artistic',
    purpose: 'entertainment'
  });

  // Apply to all state
  const [applyStyleToAll, setApplyStyleToAll] = useState(false);
  const [applyPurposeToAll, setApplyPurposeToAll] = useState(false);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus[]>([]);
  const [totalPromptsToProcess, setTotalPromptsToProcess] = useState(0);

  // Update selected types when prompt counts change (after loading prompts)
  useEffect(() => {
    if (isOpen && !isLoadingPrompts) {
      setSelectedTypes({
        music: promptCounts.music > 0,
        image: promptCounts.image > 0,
        narration: promptCounts.narration > 0,
        others: promptCounts.others > 0
      });
    }
  }, [isOpen, isLoadingPrompts, draftPrompts]);

  const handleToggleType = (type: keyof typeof selectedTypes) => {
    setSelectedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Handler to update a specific type's options
  const handleUpdateOptions = (
    type: 'music' | 'image' | 'narration' | 'others',
    field: 'style' | 'purpose',
    value: string
  ) => {
    const setter = {
      music: setMusicOptions,
      image: setImageOptions,
      narration: setNarrationOptions,
      others: setOthersOptions
    }[type];

    setter(prev => ({ ...prev, [field]: value }));

    // If "Apply to all" is enabled for this field, update all other types
    if (field === 'style' && applyStyleToAll) {
      setImageOptions(prev => ({ ...prev, style: value }));
      setNarrationOptions(prev => ({ ...prev, style: value }));
      setOthersOptions(prev => ({ ...prev, style: value }));
      setMusicOptions(prev => ({ ...prev, style: value }));
    }

    if (field === 'purpose' && applyPurposeToAll) {
      setImageOptions(prev => ({ ...prev, purpose: value }));
      setNarrationOptions(prev => ({ ...prev, purpose: value }));
      setOthersOptions(prev => ({ ...prev, purpose: value }));
      setMusicOptions(prev => ({ ...prev, purpose: value }));
    }
  };

  const handleProcess = async () => {
    setIsProcessing(true);
    setProcessingStatus([]);

    const options: BatchProcessOptions = {
      types: Object.entries(selectedTypes)
        .filter(([_, selected]) => selected)
        .map(([type]) => type),
      music: selectedTypes.music ? musicOptions : undefined,
      image: selectedTypes.image ? imageOptions : undefined,
      narration: selectedTypes.narration ? narrationOptions : undefined,
      others: selectedTypes.others ? othersOptions : undefined
    };

    // Calculate total number of draft prompts to process
    const draftsToProcess = draftPrompts.filter(prompt => {
      // Only count drafts
      const isDraft = !prompt.generatedPrompt || prompt.generatedPrompt.trim() === '';
      if (!isDraft) return false;
      
      // Check if prompt matches any selected type (using first word logic)
      const title = (prompt.title || '').toLowerCase();
      const firstWord = title.split(' ')[0];
      
      return options.types.some(type => {
        switch(type) {
          case 'music':
            return firstWord === 'music';
          case 'image':
            return firstWord === 'image';
          case 'narration':
            return firstWord === 'narration' || firstWord === 'voice';
          case 'others':
            return !['image', 'video', 'music', 'sound', 'sfx', 'narration', 'voice', 'character'].includes(firstWord);
          default:
            return false;
        }
      });
    });
    
    setTotalPromptsToProcess(draftsToProcess.length);

    try {
      // Call batch processing service directly to get status updates
      await processBatchPrompts(
        projectId,
        draftPrompts,
        options,
        (type, status, message, promptId, promptTitle) => {
          // Update status in real-time
          setProcessingStatus(prev => {
            const existing = prev.find(s => s.promptId === promptId);
            if (existing) {
              // Update existing status
              return prev.map(s =>
                s.promptId === promptId
                  ? { ...s, status, message }
                  : s
              );
            } else {
              // Add new status
              return [...prev, { type, status, message, promptId, promptTitle }];
            }
          });
        }
      );

      // Call onComplete to reload prompts
      await onComplete();
      
      setTimeout(() => {
        onClose();
        setIsProcessing(false);
        setProcessingStatus([]);
        setTotalPromptsToProcess(0);
      }, 1500);
    } catch (error) {
      console.error('Batch processing error:', error);
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{t('promptGenerator.batchProcessModal.title')}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoadingPrompts ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              gap: '16px'
            }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {t('promptGenerator.batchProcessModal.loading.prompts')}
              </p>
            </div>
          ) : !isProcessing ? (
            <>
              {availableTypes.length === 0 ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.6)'
                }}>
                  <p style={{ fontSize: '16px', marginBottom: '8px' }}>
                    {t('promptGenerator.batchProcessModal.empty.noPromptsTitle')}
                  </p>
                  <p style={{ fontSize: '14px' }}>
                    {t('promptGenerator.batchProcessModal.empty.noPromptsMessage')}
                  </p>
                </div>
              ) : (
                <>
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      {t('promptGenerator.batchProcessModal.step1.title')}
                    </h3>
                    <p style={{ 
                      fontSize: '13px', 
                      color: 'rgba(255, 255, 255, 0.6)', 
                      marginBottom: '16px',
                      marginTop: '-8px'
                    }}>
                      {t('promptGenerator.batchProcessModal.step1.subtitle')}
                    </p>
                    <div className={styles.typeGrid}>
                  {promptCounts.music > 0 && (
                    <TypeCheckbox
                      icon={faMusic}
                      label={t('promptGenerator.batchProcessModal.step1.music')}
                      count={promptCounts.music}
                      checked={selectedTypes.music}
                      onChange={() => handleToggleType('music')}
                    />
                  )}
                  {promptCounts.image > 0 && (
                    <TypeCheckbox
                      icon={faImage}
                      label={t('promptGenerator.batchProcessModal.step1.image')}
                      count={promptCounts.image}
                      checked={selectedTypes.image}
                      onChange={() => handleToggleType('image')}
                    />
                  )}
                  {promptCounts.narration > 0 && (
                    <TypeCheckbox
                      icon={faMicrophone}
                      label={t('promptGenerator.batchProcessModal.step1.narration')}
                      count={promptCounts.narration}
                      checked={selectedTypes.narration}
                      onChange={() => handleToggleType('narration')}
                    />
                  )}
                  {promptCounts.others > 0 && (
                    <TypeCheckbox
                      icon={faEllipsisH}
                      label={t('promptGenerator.batchProcessModal.step1.other')}
                      count={promptCounts.others}
                      checked={selectedTypes.others}
                      onChange={() => handleToggleType('others')}
                    />
                  )}
                </div>
              </div>

              {/* Determine which section is first (for "Apply to all" checkboxes) */}
              {(() => {
                const selectedTypesArray = Object.entries(selectedTypes)
                  .filter(([_, selected]) => selected)
                  .map(([type]) => type);
                
                let sectionIndex = 0;

                return (
                  <>
                    {selectedTypes.music && (
                      <TypeOptionsSection
                        title={t('promptGenerator.batchProcessModal.sectionTitles.musicOptions')}
                        icon={faMusic}
                        type="music"
                        options={musicOptions}
                        onChange={handleUpdateOptions}
                        applyStyleToAll={applyStyleToAll}
                        applyPurposeToAll={applyPurposeToAll}
                        onApplyStyleToAllChange={setApplyStyleToAll}
                        onApplyPurposeToAllChange={setApplyPurposeToAll}
                        isFirstSection={sectionIndex++ === 0}
                      />
                    )}

                    {selectedTypes.image && (
                      <TypeOptionsSection
                        title={t('promptGenerator.batchProcessModal.sectionTitles.imageOptions')}
                        icon={faImage}
                        type="image"
                        options={imageOptions}
                        onChange={handleUpdateOptions}
                        applyStyleToAll={applyStyleToAll}
                        applyPurposeToAll={applyPurposeToAll}
                        onApplyStyleToAllChange={setApplyStyleToAll}
                        onApplyPurposeToAllChange={setApplyPurposeToAll}
                        isFirstSection={sectionIndex++ === 0}
                      />
                    )}

                    {selectedTypes.narration && (
                      <TypeOptionsSection
                        title={t('promptGenerator.batchProcessModal.sectionTitles.narrationOptions')}
                        icon={faMicrophone}
                        type="narration"
                        options={narrationOptions}
                        onChange={handleUpdateOptions}
                        applyStyleToAll={applyStyleToAll}
                        applyPurposeToAll={applyPurposeToAll}
                        onApplyStyleToAllChange={setApplyStyleToAll}
                        onApplyPurposeToAllChange={setApplyPurposeToAll}
                        isFirstSection={sectionIndex++ === 0}
                      />
                    )}

                    {selectedTypes.others && (
                      <TypeOptionsSection
                        title={t('promptGenerator.batchProcessModal.sectionTitles.otherOptions')}
                        icon={faEllipsisH}
                        type="others"
                        options={othersOptions}
                        onChange={handleUpdateOptions}
                        applyStyleToAll={applyStyleToAll}
                        applyPurposeToAll={applyPurposeToAll}
                        onApplyStyleToAllChange={setApplyStyleToAll}
                        onApplyPurposeToAllChange={setApplyPurposeToAll}
                        isFirstSection={sectionIndex++ === 0}
                      />
                    )}
                  </>
                );
              })()}
              </>
            )}
            </>
          ) : (
            <ProcessingSection 
              status={processingStatus} 
              totalPrompts={totalPromptsToProcess}
            />
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isProcessing}
          >
            {t('promptGenerator.batchProcessModal.buttons.cancel')}
          </button>
          <button
            className={styles.processButton}
            onClick={handleProcess}
            disabled={isProcessing || availableTypes.length === 0 || Object.values(selectedTypes).every(v => !v)}
          >
            {isProcessing ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                {t('promptGenerator.batchProcessModal.buttons.processing')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheckCircle} />
                {t('promptGenerator.batchProcessModal.buttons.startProcessing')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface TypeCheckboxProps {
  icon: any;
  label: string;
  count?: number;
  checked: boolean;
  onChange: () => void;
}

const TypeCheckbox: React.FC<TypeCheckboxProps> = ({ icon, label, count, checked, onChange }) => (
  <label className={`${styles.typeCheckbox} ${checked ? styles.checked : ''}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={styles.checkbox}
    />
    <FontAwesomeIcon icon={icon} className={styles.typeIcon} />
    <span className={styles.typeLabel}>
      {count !== undefined && `(${count}) `}{label}
    </span>
  </label>
);

interface TypeOptionsSectionProps {
  title: string;
  icon: any;
  type: 'music' | 'image' | 'narration' | 'others';
  options: { style: string; purpose: string };
  onChange: (type: 'music' | 'image' | 'narration' | 'others', field: 'style' | 'purpose', value: string) => void;
  applyStyleToAll: boolean;
  applyPurposeToAll: boolean;
  onApplyStyleToAllChange: (checked: boolean) => void;
  onApplyPurposeToAllChange: (checked: boolean) => void;
  isFirstSection: boolean;
}

const TypeOptionsSection: React.FC<TypeOptionsSectionProps> = ({
  title,
  icon,
  type,
  options,
  onChange,
  applyStyleToAll,
  applyPurposeToAll,
  onApplyStyleToAllChange,
  onApplyPurposeToAllChange,
  isFirstSection
}) => {
  const { t } = useTranslation('promptGenerator');
  
  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>
        <FontAwesomeIcon icon={icon} />
        {title}
      </h3>
      <div className={styles.optionsGrid}>
        <div className={styles.optionField}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label className={styles.optionLabel}>{t('promptGenerator.batchProcessModal.title')}</label>
            {isFirstSection && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.7)',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={applyStyleToAll}
                  onChange={(e) => onApplyStyleToAllChange(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {t('promptGenerator.batchProcessModal.step2.applyToAll')}
              </label>
            )}
          </div>
          <select
            className={styles.optionSelect}
            value={options.style}
            onChange={(e) => onChange(type, 'style', e.target.value)}
          >
            <option value="cinematic">{t('promptGenerator.batchProcessModal.step2.options.cinematic')}</option>
            <option value="realistic">{t('promptGenerator.batchProcessModal.step2.options.realistic')}</option>
            <option value="animated">{t('promptGenerator.batchProcessModal.step2.options.animated')}</option>
            <option value="artistic">{t('promptGenerator.batchProcessModal.step2.options.artistic')}</option>
            <option value="professional">{t('promptGenerator.batchProcessModal.step2.options.professional')}</option>
            <option value="inspirational">{t('promptGenerator.batchProcessModal.step2.options.inspirational')}</option>
          </select>
        </div>

        <div className={styles.optionField}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label className={styles.optionLabel}>{t('promptGenerator.batchProcessModal.step3.title')}</label>
            {isFirstSection && (
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.7)',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={applyPurposeToAll}
                  onChange={(e) => onApplyPurposeToAllChange(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                {t('promptGenerator.batchProcessModal.step3.applyToAll')}
              </label>
            )}
          </div>
          <select
            className={styles.optionSelect}
            value={options.purpose}
            onChange={(e) => onChange(type, 'purpose', e.target.value)}
          >
            <option value="social-media">{t('promptGenerator.batchProcessModal.step3.options.socialMedia')}</option>
            <option value="marketing">{t('promptGenerator.batchProcessModal.step3.options.marketing')}</option>
            <option value="storytelling">{t('promptGenerator.batchProcessModal.step3.options.storytelling')}</option>
            <option value="education">{t('promptGenerator.batchProcessModal.step3.options.education')}</option>
            <option value="entertainment">{t('promptGenerator.batchProcessModal.step3.options.entertainment')}</option>
            <option value="religious">{t('promptGenerator.batchProcessModal.step3.options.religious')}</option>
          </select>
        </div>
      </div>
    </div>
  );
};

interface ProcessingSectionProps {
  status: ProcessingStatus[];
  totalPrompts: number;
}

const ProcessingSection: React.FC<ProcessingSectionProps> = ({ status, totalPrompts }) => {
  const { t } = useTranslation('promptGenerator');
  
  // Calculate progress
  const completed = status.filter(s => s.status === 'completed' || s.status === 'error').length;
  
  return (
    <div className={styles.processingSection}>
      <h3 className={styles.processingTitle}>
        {t('promptGenerator.batchProcessModal.processing.title')} ({completed} {t('promptGenerator.batchProcessModal.processing.of')} {totalPrompts} {t('promptGenerator.batchProcessModal.processing.completed')})
      </h3>
      <div className={styles.statusList}>
        {status.map((item, index) => (
          <div key={index} className={styles.statusItem}>
            <FontAwesomeIcon
              icon={item.status === 'completed' ? faCheckCircle : faSpinner}
              spin={item.status === 'processing'}
              className={`${styles.statusIcon} ${
                item.status === 'completed' ? styles.completed : styles.processing
              }`}
            />
            <div style={{ flex: 1 }}>
              <div className={styles.statusText}>
                {item.promptTitle || `${item.type.charAt(0).toUpperCase() + item.type.slice(1)} prompt`}
              </div>
              <span className={styles.statusMessage}>{item.message}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BatchProcessModal;
